/**
 * ResultsPage - Display completed session results with player statistics
 * Shows summary banner, results table, and export options
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionsService } from '@/services/sessions';
import { SessionResultsResponse } from '@/types/session';
import { TeacherGameMonitor } from '@/game/components/TeacherGameMonitor';
import { adaptGameStateResponse } from '@/game/utils/gameStateAdapter';
import { GameState } from '@/game/models/GameState';

export function ResultsPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<SessionResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);

  useEffect(() => {
    loadResults();
  }, [sessionId]);

  async function loadResults() {
    if (!sessionId) {
      setError('Session ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get session results from API
      const data = await sessionsService.getSessionResults(sessionId);
      setData(data);

      // Also fetch game state for full session review
      try {
        const gameStateResponse = await sessionsService.getGameState(sessionId);
        const adapted = adaptGameStateResponse(gameStateResponse);
        if (adapted) {
          setGameState(adapted);
        }
      } catch (err) {
        console.error('Failed to load game state:', err);
        // Non-fatal - still show basic results
      }
    } catch (err: any) {
      console.error('Failed to load results:', err);
      setError(
        err.response?.data?.message || 'Failed to load session results'
      );
    } finally {
      setLoading(false);
    }
  }

  function calculateDuration(startedAt?: string, completedAt?: string): string {
    if (!startedAt || !completedAt) return 'N/A';

    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  function formatDateTime(isoString: string | undefined): string {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  async function handleExportCSV() {
    if (!data) return;

    try {
      setExportLoading(true);

      const { session, results } = data;
      const csvRows: string[] = [];

      // Header row
      csvRows.push('Player,Display Name,Full Name,Email,Role,Score,Energy');

      // Data rows
      results.forEach((result, index) => {
        csvRows.push(
          [
            index + 1,
            result.displayName,
            result.fullName || '',
            result.email || '',
            result.role,
            result.score,
            result.energy,
          ].join(',')
        );
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `session-${session.code}-results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black" />
            <p className="mt-4 text-black">Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl shadow-sm px-6 py-4 mb-6">
            <strong className="text-red-900">Error:</strong> <span className="text-red-700">{error || 'Results not found'}</span>
          </div>
          <button
            onClick={() => navigate('/login/teacher')}
            className="btn btn-secondary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { session, results } = data;
  const duration = calculateDuration(session.startedAt, session.completedAt);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Banner */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-black">Session Results</h1>
            <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-semibold text-sm">COMPLETE</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Join Code</label>
              <p className="text-2xl font-bold text-black">{session.code}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Session Name</label>
              <p className="text-sm font-semibold text-black">{session.sessionName || 'Unnamed Session'}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Started</label>
              <p className="text-sm text-black">{formatDateTime(session.startedAt)}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Completed</label>
              <p className="text-sm text-black">{formatDateTime(session.completedAt)}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Duration</label>
              <p className="text-lg font-semibold text-black">{duration}</p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <label className="text-xs text-gray-600 font-medium block mb-1">Players</label>
              <p className="text-lg font-semibold text-black">{results.length}</p>
            </div>
          </div>
        </div>

        {/* Export Actions */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-4 mb-6">
          <button
            onClick={handleExportCSV}
            disabled={exportLoading}
            className="btn btn-primary"
          >
            {exportLoading ? 'Exporting...' : 'Export to CSV'}
          </button>
        </div>

        {/* Game Monitor - Full Session Review */}
        {gameState && (
          <div className="mb-6">
            <div className="bg-green-50 border border-green-200 rounded-t-xl px-4 py-2">
              <span className="text-green-800 font-semibold">
                Session Complete - Final Game State
              </span>
            </div>
            <TeacherGameMonitor
              gameState={gameState}
              className="rounded-t-none"
            />
          </div>
        )}

        {/* Results Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-6 mb-6">
          <h2 className="text-2xl font-bold text-black mb-4">Player Results</h2>

          {results.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No player results available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Display Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Full Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Score</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Energy</th>
                  </tr>
                </thead>
                <tbody>
                  {results
                    .sort((a, b) => b.score - a.score)
                    .map((result, index) => {
                      // Get actual energy from game state if available
                      const gamePlayer = gameState?.players.find(p => p.playerId === result.playerId);
                      const finalEnergy = gamePlayer?.energy ?? result.energy;

                      return (
                        <tr key={result.playerId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-bold text-gray-900">{index + 1}</td>
                          <td className="py-3 px-4 text-sm font-medium text-black">{result.displayName}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{result.fullName || '—'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{result.email || '—'}</td>
                          <td className="py-3 px-4">
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-lg">{result.role}</span>
                          </td>
                          <td className="py-3 px-4 text-lg font-bold text-black">{result.score}</td>
                          <td className="py-3 px-4 text-sm text-gray-900">{finalEnergy}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Page Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/login/teacher/sessions/${sessionId}`)}
            className="btn btn-secondary"
          >
            Back to Session
          </button>
          <button onClick={() => navigate('/login/teacher')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
